/**
 * The blob-storage config files, as editable rows.
 *
 * Each file is one flat-ish JSON object read from
 * `utility/file/{container}/{file}` and written back whole by
 * `utility/upload`. The rows below are that object turned inside out, with the
 * one thing the original threw away kept: the *type* of each value.
 */

export const CONFIG_FILES = [
  { key: 'server', container: 'configs', file: 'server.json' },
  { key: 'app', container: 'configs', file: 'app.json' },
  { key: 'manager', container: 'configs', file: 'manager.json' },
] as const;

export type ConfigFileKey = (typeof CONFIG_FILES)[number]['key'];

/** What a value is, so it can be written back as what it was. */
export type ValueKind = 'string' | 'number' | 'boolean' | 'json';

export interface SettingRow {
  key: string;
  /** The editable representation; always text, whatever the kind. */
  text: string;
  kind: ValueKind;
}

/**
 * A JSON object as rows.
 *
 * The original did `` `${value}` `` on everything, so a saved file turned
 * `{"maxItems": 50, "debug": true}` into `{"maxItems": "50", "debug": "true"}`
 * — every number and boolean in the file became a quoted string the first time
 * anybody pressed Save, and a nested object became the literal text
 * `[object Object]`, destroying it.
 */
export function toRows(source: Record<string, unknown>): SettingRow[] {
  return Object.entries(source).map(([key, value]) => {
    if (typeof value === 'string') return { key, text: value, kind: 'string' };
    if (typeof value === 'number')
      return { key, text: String(value), kind: 'number' };
    if (typeof value === 'boolean')
      return { key, text: String(value), kind: 'boolean' };
    // null, arrays and objects keep their JSON so nesting survives a round trip.
    return { key, text: JSON.stringify(value, null, 2), kind: 'json' };
  });
}

/** Whether a row's text still means what its kind says. */
export function rowError(row: SettingRow): 'key' | 'value' | null {
  if (!row.key.trim()) return 'key';
  switch (row.kind) {
    case 'number':
      return row.text.trim() !== '' && Number.isFinite(Number(row.text))
        ? null
        : 'value';
    case 'boolean':
      return row.text === 'true' || row.text === 'false' ? null : 'value';
    case 'json':
      try {
        JSON.parse(row.text);
        return null;
      } catch {
        return 'value';
      }
    default:
      return null;
  }
}

function toValue(row: SettingRow): unknown {
  switch (row.kind) {
    case 'number':
      return Number(row.text);
    case 'boolean':
      return row.text === 'true';
    case 'json':
      return JSON.parse(row.text);
    default:
      return row.text;
  }
}

/** Rows back to the file's text. Only called once every row validates. */
export function toFileText(rows: SettingRow[]): string {
  const object: Record<string, unknown> = {};
  for (const row of rows) object[row.key] = toValue(row);
  // `JSON.stringify` rather than the original's hand-built string, which
  // concatenated `"key": "value"` without escaping — one quote or backslash in
  // a value and the file it wrote was not valid JSON.
  return JSON.stringify(object, null, 2);
}

/**
 * The kind a newly typed value looks like.
 *
 * Only used for keys the person is adding; existing keys keep whatever they
 * already were. The row shows the result, so a guess is visible before saving.
 */
export function inferKind(text: string): ValueKind {
  const trimmed = text.trim();
  if (trimmed === 'true' || trimmed === 'false') return 'boolean';
  if (trimmed !== '' && Number.isFinite(Number(trimmed))) return 'number';
  if (/^[[{]/.test(trimmed)) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      return 'string';
    }
  }
  return 'string';
}
